import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import EmptyView from 'general/components/Others/EmptyView';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { getMonthFromPeriodKey, resolveReconciliationPeriodKey } from 'features/ReconciliationReport/reconciliationWeekHelper';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import UIHelper from 'general/helpers/UIHelper';
import AppRoute from 'general/constants/AppRoute';
import Utils from 'general/utils/Utils';
import ChargeHelper from 'features/Charge/chargeHelper';
import SystemHelper from 'features/System/systemHelper';
import PropTypes from 'prop-types';
import AppColors from 'general/constants/AppColors';

EnergyBillView.propTypes = {
    agencyId: PropTypes.string,
    primaryRole: PropTypes.string,
    energyBillList: PropTypes.array,
    energyBillTotal: PropTypes.number,
    onFilterChanged: PropTypes.func,
    filter: PropTypes.object,
    reconciliationDetail: PropTypes.object,
};

const defaultColumnIds = ['STATION_CODE', 'STATION_NAME', 'STATUS', 'ENERGY', 'DIFFERENCE', 'AMOUNT_BEFORE_TAX', 'AMOUNT_AFTER_TAX', 'INVOICE', 'VALID_STATUS'];

function EnergyBillView(props) {
    // ---- params ----
    const router = useRouter();
    const { t } = useTranslation();
    const { agencyId, primaryRole, onFilterChanged, filter, reconciliationDetail } = props;
    const { user } = useSelector((state) => state.auth);
    const [energyBillList, setEnergyBillList] = useState([]);
    const [energyBillTotal, setEnergyBillTotal] = useState([]);
    const [monthlyEnergyList, setMonthlyEnergyList] = useState([]);

    const fullColumns = useMemo(() => {
        return [
            {
                id: 'STATION_CODE',
                name: UIHelper.buildTableColumn('Mã trạm sạc'),
                selector: (row) => primaryRole === 'INVESTOR' ? row?.station?.stationCode : row?.stationCode ?? row?.stationId,
                minWidth: '120px',
                style: tableStyle.columns.normal,
                sortable: true,
            },
            {
                id: 'STATION_NAME',
                name: UIHelper.buildTableColumn('Tên trạm sạc'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            Utils.openInNewTab(`${AppRoute.stationList}/${primaryRole === 'INVESTOR' ? row?.station?.stationId : row?.stationId}`);
                        }}
                    >
                        {primaryRole === 'INVESTOR' ? row?.station?.name : row?.name}
                    </span>
                ),
                minWidth: '230px',
                style: tableStyle.columns.link,
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái hoạt động'),
                minWidth: '150px',
                selector: (row) => (
                    <KTLabel
                        text={ChargeHelper.Utils.getChargeStationStatusName(primaryRole === 'INVESTOR' ? row?.station?.status : row?.status)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${ChargeHelper.Utils.getChargeStationStatusColor(
                            primaryRole === 'INVESTOR' ? row?.station?.status : row?.status
                        )}`}
                    />
                ),
            },
            {
                id: 'ENERGY',
                name: UIHelper.buildTableColumn('Sản lượng điện tiêu thụ', 'kWh'),
                selector: (row) => {
                    return Utils.formatNumber(row?.evnEnergyUsed ?? 0);
                },
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'DIFFERENCE',
                name: UIHelper.buildTableColumn('Chênh lệch với hệ thống', '%'),
                selector: (row) => {
                    const totalEnergy = monthlyEnergyList?.find(energy => energy?.stationId === row?.station?.stationId)?.totalEnergy ?? 0;
                    const difference = row?.evnEnergyUsed ? (row?.evnEnergyUsed - (totalEnergy / 1000)) / row?.evnEnergyUsed * 100 : 0;
                    return Number(difference?.toFixed(3));
                },
                style: tableStyle.columns.normal,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'AMOUNT_BEFORE_TAX',
                name: UIHelper.buildTableColumn('Hóa đơn tiền điện (Trước thuế)', 'VNĐ'),
                selector: (row) =>
                    Utils.isValid(row?.amountBeforeTax)
                        ? Utils.formatNumber(row?.amountBeforeTax)
                        : 0,
                style: tableStyle.columns.normal,
                minWidth: '210px',
                right: true,
            },
            {
                id: 'AMOUNT_AFTER_TAX',
                name: UIHelper.buildTableColumn('Hóa đơn tiền điện (Sau thuế)', 'VNĐ'),
                selector: (row) =>
                    !!row?.amount
                        ? Utils.formatNumber(row?.amount)
                        : Utils.formatNumber(row?.amountBeforeTax * (1 + row?.taxRate)),
                style: tableStyle.columns.normal,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'INVOICE',
                name: UIHelper.buildTableColumn('Hóa đơn từ điện lực'),
                selector: (row) => {
                    const attachedFiles = row?.energyAccounts?.[0]?.energyBills?.[0]?.evnInvoiceDocument || [];

                    const documentArray = Array.isArray(attachedFiles) ? attachedFiles : attachedFiles ? [attachedFiles] : [];
                    const documentCount = documentArray.length;

                    return documentCount > 0 ? (
                        <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                const firstDocument = documentArray[0];
                                if (firstDocument) {
                                    Utils.openInNewTab(Utils.getFullUrl(firstDocument));
                                }
                            }}
                        >
                            Xem chi tiết
                        </span>
                    ) : null;
                },
                style: tableStyle.columns.link,
                minWidth: '200px',
            },
            {
                id: 'VALID_STATUS',
                name: UIHelper.buildTableColumn('Trạng thái hóa đơn'),
                selector: (row) => (
                    <KTLabel
                        text={SystemHelper.Utils.EnergyBill.getEnergyBillStatusName(
                            row?.status
                        )}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${SystemHelper.Utils.EnergyBill.getEnergyBillStatusColor(
                            row?.status
                        )}`}
                    />
                ),
                style: tableStyle.columns.normal,
                width: '140px',
            },
        ];
    }, [t, monthlyEnergyList]);

    useEffect(() => {
        const params = {
            month: getMonthFromPeriodKey(resolveReconciliationPeriodKey(reconciliationDetail))
                || (reconciliationDetail?.month?.length === 7 ? reconciliationDetail?.month : ''),
        }
        if (primaryRole === 'LANDLORD') {
            params.landlordId = reconciliationDetail?.agencyId;
        } else {
            params.agencyId = reconciliationDetail?.agencyId;
        }
        SystemHelper.API.EnergyBill.requestGetAll(params)
            .then((res) => {
                setEnergyBillList(res?.energyBills);
                setEnergyBillTotal(res?.total);
            })
            .catch((err) => { });
    }, []);
    useEffect(() => {
        ChargeHelper.API.Dashboard.requestGetMonthlyStationEnergy({
            month: getMonthFromPeriodKey(resolveReconciliationPeriodKey(reconciliationDetail))
                || (reconciliationDetail?.month?.length === 7 ? reconciliationDetail?.month : ''),
        })
            .then((res) => {
                setMonthlyEnergyList(res);
            })
            .catch((err) => { });
    }, []);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = `${router.pathname}${router.location.hash}`;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id).filter(Boolean));
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        saveChanges,
        onSelectAll,
        onUnSelectAll,
        fullColumnsIdSorted
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    // ---- methods ----

    const handleFilterChanged = (filter) => {
        if (onFilterChanged) {
            onFilterChanged(filter);
        }
    };

    return (
        <div>
            <div>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Tổng tiền điện:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '} {Utils.formatNumber(Math.round(_.sumBy(energyBillList, 'amountBeforeTax')) ?? 0)} VNĐ
                </span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[500])}>{' '}•{' '}</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Thuế GTGT:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '}
                    {Utils.formatNumber(
                        Math.round(
                            energyBillList?.reduce((sum, bill) => {
                                return sum + bill?.amountBeforeTax * bill?.taxRate;
                            }, 0)
                        ) ?? 0
                    )} VNĐ
                </span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[500])}>{' '}•{' '}</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Tổng tiền điện sau thuế:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '}
                    {Utils.formatNumber(
                        Math.round(
                            energyBillList?.reduce((sum, bill) => {
                                return sum + bill?.amountBeforeTax * (1 + bill?.taxRate);
                            }, 0)
                        ) ?? 0
                    )} VNĐ
                </span>
            </div>
            {/* Body */}
            <div className="pt-4">
                <KTTable
                    columns={columns}
                    items={energyBillList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={() => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(rows) => { }}
                    hasActionColumn={false}
                    noDataComponent={
                        <EmptyView
                            icon={imgEmptyNoData}
                            title="Không có dữ liệu để hiển thị"
                            text={
                                <div className="text-center">
                                    <span>
                                        Hiện không có dữ liệu để hiển thị. Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.
                                    </span>
                                </div>
                            }
                        />
                    }
                />
            </div>

            {/* Phân trang */}
            {!_.isEmpty(energyBillList) && (
                <div className="mt-4">
                    <KTPagination
                        currentPage={filter.page + 1}
                        rowCount={energyBillTotal}
                        rowsPerPage={filter.limit}
                        currentItemInPageCount={energyBillList.length}
                        onChangePage={(newPage) => {
                            const iNewPage = parseInt(newPage);
                            handleFilterChanged({
                                page: iNewPage - 1,
                            });
                        }}
                        onChangeRowsPerPage={(newRowsPerPage) => {
                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                            handleFilterChanged({
                                limit: iNewRowsPerPage,
                                page: 0,
                            });
                        }}
                        loading={false}
                        color={KTPaginationColor.success}
                        appendText="đại lý"
                    />
                </div>
            )}
        </div>
    );
}

export default EnergyBillView;
